import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { get, isNil } from 'lodash-es';
import * as yup from 'yup';

import { commissionPercentageSchema } from '~components/PayCommission/commissionPercentageSchema';
import {
  nameIdNonRequiredSchema,
  nameIdSchemaRequired,
  notesSchema,
  phoneSchema,
  waypointSchemaOptionalSites,
} from '~constants/regexConst';
import { CompanyBasic } from '~hooks/useCompany/models';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import { Project } from '~hooks/useProjects/models';
import { TaxRateScheduleBasic } from '~hooks/useTaxRateSchedule/models';
import { RateDetailsDTO } from '~pages/Sales/Orders/NewOrderForm/schema';
import {
  makeMaterialRateRequirement,
  makeRatePriceSchema,
  requiredMessage,
} from '~pages/Sales/sharedSales/rateRequirements';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { formQuantityFieldRepresentsEmptyQuantity } from '~utils/utilFunctions';

import { fileAttachmentSchema } from '../../../formsShared/components/FileAttachments';

const emptyStringOrNumberNotRequiredSchema = yup.lazy((val) => {
  return String(val).trim() === ''
    ? yup.string().notRequired()
    : yup.number().notRequired();
});

const projectMaterialTotalSiteSchema = nameIdNonRequiredSchema.nullable().notRequired();

export type TaxRateScheduleFormValue = {
  label: string;
  value: string;
  rate?: Nullable<string>;
  _entity?: { rate: Nullable<string> };
};

const taxRateScheduleSchema = yup
  .mixed<TaxRateScheduleFormValue>()
  .nullable()
  .notRequired();

const projectMaterialTotalSchema = yup.object().shape({
  material: nameIdNonRequiredSchema,
  unitOfMeasure: nameIdNonRequiredSchema,
  totalQuantity: emptyStringOrNumberNotRequiredSchema,
  id: yup.string(),
  isDeleted: yup.boolean().notRequired(),
  phase: yup
    .object()
    .shape({
      id: yup.string().nullable().notRequired(),
      name: yup.string().required(),
      code: yup.string().nullable().notRequired(),
    })
    .nullable()
    .notRequired(),
  site: projectMaterialTotalSiteSchema,
});

const projectMaterialRateSchema = yup.object().shape({
  id: yup.string().notRequired(),
  isDeleted: yup.boolean().notRequired(),
  parentIndex: yup.number().notRequired(),
  material: nameIdNonRequiredSchema,
  unitOfMeasure: nameIdNonRequiredSchema.when(
    ['materialRateSite', 'materialRateBuyPrice', 'materialRateSellPrice', 'isDeleted'],
    {
      is: (
        site: { id?: string } | null | undefined,
        buy: string | number | undefined,
        sell: string | number | undefined,
        isDeleted: boolean | undefined,
      ) =>
        !isDeleted &&
        (Boolean(site?.id) ||
          (buy !== undefined && String(buy).trim() !== '') ||
          (sell !== undefined && String(sell).trim() !== '')),
      then: () =>
        nameIdSchemaRequired.required(
          t('form_validation_errors.required', {
            field: t('form_fields.unit'),
          }),
        ),
    },
  ),
  materialRateSite: nameIdNonRequiredSchema.notRequired(),
  materialRateBuyPrice: yup
    .number()
    .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
    .min(0)
    .typeError('Numbers only.')
    .notRequired(),
  materialRateSellPrice: yup
    .number()
    .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
    .min(0)
    .typeError('Numbers only.')
    .notRequired(),
});

export const EmptyContactItem = {
  id: '',
  isDeleted: false,
  name: '',
  phone: '',
  type: {
    id: '',
    name: '',
  },
};

export const EmptyProjectMaterialTotalItem = {
  id: '',
  isDeleted: false,
  material: {
    id: '',
    name: '',
  },
  totalQuantity: '',
  unitOfMeasure: {
    id: '',
    name: '',
  },
  phase: null as { id: string; name: string; code?: string } | null,
  site: null as { id: string; name: string } | null,
};

export const EmptyMaterialRateItem = {
  id: '',
  isDeleted: false,
  parentIndex: 0,
  material: { id: '', name: '' },
  unitOfMeasure: { id: '', name: '' },
  materialRateSite: { id: '', name: '' },
  materialRateBuyPrice: '' as string | number,
  materialRateSellPrice: '' as string | number,
};

export const projectFormSchema = yup.object().shape(
  {
    id: yup.string(),
    company: yup
      .object()
      .shape({
        legalName: yup.string(),
        id: yup.string(),
      })
      .required(),
    name: yup.string().required(
      `${t('form_validation_errors.required', {
        field: t('project.form.project_name'),
      })}`,
    ),
    effectiveAt: yup.string().notRequired(),
    expiresAt: yup
      .string()
      .notRequired()
      .test(
        'is-before-date',
        `${t('form_validation_errors.date_before', {
          field: 'date',
          date: 'effective at',
        })}`,
        (value, context) => {
          if (!context.parent.effectiveAt || !value) return true;
          return dayjs(value).isAfter(context.parent.effectiveAt);
        },
      ),
    externalId: yup.string(),
    contract_number: yup.string(),
    dispatch_number: yup.string(),
    notes: notesSchema,
    projectNotes: notesSchema,
    orderNotes: notesSchema,
    jobNotes: notesSchema,
    internalNotes: notesSchema,
    projectTerms: yup.string().max(4000).default(''),
    account: nameIdSchemaRequired,
    poJobNumber: yup.string(),
    state: yup.string(),
    site: nameIdNonRequiredSchema,
    projectMaterialTotals: yup
      .array()
      .of(projectMaterialTotalSchema.notRequired())
      .notRequired(),
    materialRates: yup.array().of(projectMaterialRateSchema).notRequired(),
    allContacts: yup.array().of(
      yup.object().shape(
        {
          name: yup
            .string()
            .trim()
            .when('phone', {
              is: (phone: string) => Boolean(phone?.trim()),
              then: () =>
                yup
                  .string()
                  .trim()
                  .required(
                    `${t('form_validation_errors.name_required_when_phone_provided')}`,
                  ),
            }),
          phone: phoneSchema,
          type: nameIdNonRequiredSchema.when(['name', 'phone'], {
            is: (name: string | null, phone: string) =>
              Boolean(name) || Boolean(phone?.trim()),
            then: () =>
              nameIdSchemaRequired.required(
                `${t('form_validation_errors.contact_type_required_when_name_or_phone_provided')}`,
              ),
          }),
          isDeleted: yup.boolean().notRequired(),
        },
        [['name', 'phone']],
      ),
    ),
    phases: yup.array().of(
      yup.object().shape({
        name: yup.string(),
        id: yup.string(),
        code: yup.string(),
        primary: yup.boolean().notRequired(),
        isDeleted: yup.boolean().notRequired(),
      }),
    ),
    department: yup
      .object()
      .shape({
        name: yup.string(),
        id: yup.string(),
      })
      .nullable(),
    foremen: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    serviceClass: nameIdNonRequiredSchema.nullable(),
    chargeToAccount: nameIdNonRequiredSchema.notRequired(),
    jobTime: yup.lazy((value) => {
      const isEmpty = isNil(value) || !String(value).trim().length;

      if (isEmpty) {
        return yup.string().notRequired();
      }

      return yup.number().min(0).required();
    }),
    orderQuantity: yup
      .number()
      .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
      .notRequired()
      .min(
        0,
        `${t('form_validation_errors.min', {
          field: t('form_fields.quantity'),
          count: 0,
        })}`,
      )
      .typeError(
        `${t('form_validation_errors.should_be_number', {
          field: t('form_fields.quantity'),
        })}`,
      ),
    orderUnitOfMeasure: yup.object().when('orderQuantity', {
      is: (orderQuantity: string | number | null) => {
        return formQuantityFieldRepresentsEmptyQuantity(orderQuantity);
      },
      then: () => nameIdNonRequiredSchema.notRequired(),
      otherwise: () => nameIdSchemaRequired.required(),
    }),
    equipmentType: nameIdNonRequiredSchema.nullable(),
    equipmentTypeGrossCapacity: emptyStringOrNumberNotRequiredSchema,
    equipmentTypeGrossCapacityUnitOfMeasure: yup
      .object()
      .when('equipmentTypeGrossCapacity', {
        is: (equipmentTypeGrossCapacity: string | number | null) => {
          return formQuantityFieldRepresentsEmptyQuantity(equipmentTypeGrossCapacity);
        },
        then: () => nameIdNonRequiredSchema.notRequired(),
        otherwise: () => nameIdSchemaRequired.required(),
      }),
    truckCount: yup.lazy((value) => {
      const isEmpty = isNil(value) || !String(value).trim().length;

      if (isEmpty) {
        return yup.string().notRequired();
      }

      return yup.number().min(0).required();
    }),
    unitsPerHour: yup
      .number()
      .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
      .typeError('Numbers only.')
      .notRequired(),
    jobQuantity: yup.number().when('jobUnitOfMeasure', {
      is: (uom: ItemNameAndId | null) =>
        isNil(uom) || uom?.id === OrderUnitOfMeasure.LOAD,
      then: (schema) => {
        return yup.lazy((val) => {
          if (String(val).trim() === '' || isNil(val)) {
            return yup
              .string()
              .notRequired()
              .transform(() => null);
          }
          return schema.min(0).required();
        });
      },
      otherwise: (schema) => {
        return yup.lazy((val) => {
          if (String(val).trim() === '' || isNil(val)) {
            return yup.string().required(
              `${t('form_validation_errors.required', {
                field: t('form_fields.job_quantity'),
              })}`,
            );
          }
          return schema.min(0.01).required();
        });
      },
    }),
    jobUnitOfMeasure: yup.object().when('jobQuantity', {
      is: (jobQuantity: string | number | null) => {
        return formQuantityFieldRepresentsEmptyQuantity(jobQuantity);
      },
      then: () => nameIdNonRequiredSchema.notRequired(),
      otherwise: () => nameIdSchemaRequired.required(),
    }),
    collaborators: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    pickUpWayPoint: waypointSchemaOptionalSites
      .shape({ _destroy: yup.boolean().notRequired() })
      .notRequired(),
    dropOffWayPoint: waypointSchemaOptionalSites
      .shape({ _destroy: yup.boolean().notRequired() })
      .notRequired(),
    stagingWayPoint: waypointSchemaOptionalSites
      .shape({ _destroy: yup.boolean().notRequired() })
      .notRequired(),
    fileAttachments: fileAttachmentSchema,
    requester: yup.object().shape({ id: yup.string() }).notRequired(),
    haulerRateType: yup
      .object()
      .shape({ label: yup.string().required(), value: yup.string().required() })
      .notRequired(),
    loadUnitOfMeasure: nameIdSchemaRequired.notRequired(),
    recurringLoads: yup.boolean().notRequired(),
    chaseMissingTickets: yup.boolean().notRequired(),
    liveEta: yup.boolean().notRequired(),
    defaultDigitalTicketView: yup.boolean().notRequired(),
    prevailingWage: yup.boolean().notRequired(),
    applyRateChangesToExistingOrders: yup.boolean().notRequired(),
    vendorPayCommissionPercentage: commissionPercentageSchema,
    driverPayCommissionPercentage: commissionPercentageSchema,
    materialRateItemized: yup.boolean().notRequired(),
    taxRateSchedule: taxRateScheduleSchema,
    taxRate: yup
      .number()
      .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
      .min(0)
      .max(100)
      .notRequired()
      .nullable(),
    taxFreight: yup.boolean().notRequired(),
    taxMaterial: yup.boolean().notRequired(),
    taxAddOns: yup.boolean().notRequired(),
    salespeople: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
  },
  [['jobUnitOfMeasure', 'jobQuantity']],
);

export const setDefaultProjectValues = (
  defaultProject: Project,
  company?: Nullable<CompanyBasic>,
  chaseMissingTicketsDefaultOnSettingEnabled?: boolean,
  userCompanyDefaultProjectTerms?: Nullable<string>,
  selectiveRateApplicationDefaultEnabled?: boolean,
  liveEtaDefault?: boolean,
  recordCompanySettings?: CompanySettings,
) => {
  const pickUpWayPoint = defaultProject?.pickUpWayPoints?.[0];
  const dropOffWayPoint = defaultProject?.dropOffWayPoints?.[0];
  const stagingWayPoint = defaultProject?.stagingWayPoints?.[0];

  let effectiveAt = defaultProject?.effectiveAt || '';

  if (!defaultProject) {
    effectiveAt = dayjs.tz().set('hours', dayjs().hour()).add(1, 'hours').startOf('hour');
  }

  return {
    id: defaultProject?.id || '',
    company: defaultProject?.company
      ? {
          legalName: defaultProject?.company?.legalName || '',
          id: defaultProject?.company?.id || '',
        }
      : company || null,
    name: defaultProject?.name || '',
    expiresAt: defaultProject?.expiresAt || '',
    effectiveAt: effectiveAt,
    poJobNumber: defaultProject?.poJobNumber || '',
    account: defaultProject?.account || null,
    externalId: defaultProject?.externalId || '',
    contract_number: defaultProject?.contractNumber || '',
    dispatch_number: defaultProject?.dispatchNumber?.name || '',
    notes: get(defaultProject, 'notes', ''),
    projectNotes: defaultProject?.notes || '',
    orderNotes: defaultProject?.orderNotes || '',
    jobNotes: defaultProject?.jobNotes || '',
    internalNotes: defaultProject?.internalNotes || '',
    // On create: pre-fill with the company default so the user sees what will ship
    // on the Quote PDF and can override before save. Saving denormalizes the
    // snapshot to project.project_terms — quote terms are treated as a frozen
    // template-clone, not a live link to the company default.
    // On edit: show whatever was previously saved (or blank — the BE-computed
    // effective_project_terms still falls back to the company default at render
    // time if the user clears the override).
    projectTerms: defaultProject
      ? defaultProject.projectTerms || ''
      : userCompanyDefaultProjectTerms || '',
    state: defaultProject?.state || '',
    site: defaultProject?.site || {},
    projectMaterialTotals: defaultProject?.projectMaterialTotals?.length
      ? defaultProject?.projectMaterialTotals.map((single) => {
          return {
            id: single.id || undefined,
            material: single.material || undefined,
            unitOfMeasure: single.unitOfMeasure || undefined,
            isDeleted: false,
            totalQuantity: single.totalQuantity || '',
            phase: single.phase
              ? {
                  id: single.phase.id,
                  name: single.phase.name,
                  code: single.phase.code,
                }
              : null,
            site: single.site ? { id: single.site.id, name: single.site.name } : null,
          };
        })
      : [EmptyProjectMaterialTotalItem],
    materialRates: [] as (typeof EmptyMaterialRateItem)[],
    allContacts: defaultProject?.allContacts?.length
      ? defaultProject?.allContacts.map((single) => {
          return {
            id: single.id || undefined,
            name: single.name || '',
            phone: single.phone || '',
            type: single.type || undefined,
            isDeleted: false,
          };
        })
      : [EmptyContactItem],
    phases: defaultProject?.phases?.length
      ? defaultProject?.phases.map((phase) => {
          return {
            name: phase.name,
            id: phase.id,
            code: phase.code,
            primary: phase.primary,
            isDeleted: false,
          };
        })
      : [],
    department: defaultProject?.department || null,
    foremen: defaultProject?.foremen || [],
    serviceClass: defaultProject?.serviceClass,
    chargeToAccount: defaultProject?.chargeToAccount
      ? { id: '', name: defaultProject.chargeToAccount }
      : null,
    jobTime: isNil(defaultProject?.jobTime) ? null : Number(defaultProject.jobTime / 60),
    orderQuantity: defaultProject?.orderQuantity,
    orderUnitOfMeasure: defaultProject?.orderUnitOfMeasure || null,
    equipmentType: defaultProject?.equipmentType || null,
    equipmentTypeGrossCapacity: defaultProject?.equipmentTypeGrossCapacity,
    equipmentTypeGrossCapacityUnitOfMeasure:
      defaultProject?.equipmentTypeGrossCapacityUnitOfMeasure || null,
    truckCount: defaultProject?.truckCount,
    unitsPerHour: defaultProject?.unitsPerHour,
    jobQuantity: defaultProject?.jobQuantity,
    jobUnitOfMeasure: defaultProject?.jobUnitOfMeasure || null,
    collaborators: defaultProject?.collaborators ?? [],
    // FIX: Check for site.id instead of waypoint.id to support Copy mode
    // When copying, waypoint IDs are stripped but site data should be preserved
    pickUpWayPoint: pickUpWayPoint?.site?.id
      ? {
          id: pickUpWayPoint?.id || '',
          type: pickUpWayPoint?.type || '',
          ordinality: pickUpWayPoint?.ordinality ?? 10,
          contact: {
            name: pickUpWayPoint?.contact?.name || '',
            phone: pickUpWayPoint?.contact?.phone || '',
          },
          site: {
            id: pickUpWayPoint?.site?.id || '',
            name: pickUpWayPoint?.site?.name || '',
          },
          discardedAt: pickUpWayPoint?.site?.discardedAt?.toISOString() ?? null,
        }
      : null,
    // FIX: Check for site.id instead of waypoint.id to support Copy mode
    dropOffWayPoint: dropOffWayPoint?.site?.id
      ? {
          id: dropOffWayPoint?.id || '',
          type: dropOffWayPoint?.type || '',
          ordinality: dropOffWayPoint?.ordinality ?? 10,
          contact: {
            name: dropOffWayPoint?.contact?.name || '',
            phone: dropOffWayPoint?.contact?.phone || '',
          },
          site: {
            id: dropOffWayPoint?.site?.id || '',
            name: dropOffWayPoint?.site?.name || '',
          },
          discardedAt: dropOffWayPoint?.site?.discardedAt?.toISOString() ?? null,
        }
      : null,
    stagingWayPoint: stagingWayPoint?.site?.id
      ? {
          id: stagingWayPoint?.id || '',
          type: stagingWayPoint?.type || '',
          ordinality: stagingWayPoint?.ordinality ?? 15,
          contact: {
            name: stagingWayPoint?.contact?.name || '',
            phone: stagingWayPoint?.contact?.phone || '',
          },
          site: {
            id: stagingWayPoint?.site?.id || '',
            name: stagingWayPoint?.site?.name || '',
          },
          discardedAt: stagingWayPoint?.site?.discardedAt?.toISOString() ?? null,
        }
      : null,
    requester: defaultProject?.requester || {},
    haulerRateType: defaultProject?.haulerRateType
      ? {
          label: getRateTypeLabel(defaultProject.haulerRateType),
          value: defaultProject.haulerRateType,
        }
      : null,
    loadUnitOfMeasure: defaultProject?.loadUnitOfMeasure || null,
    recurringLoads: defaultProject?.recurringLoads ?? false,
    chaseMissingTickets: defaultProject
      ? (defaultProject.chaseMissingTickets ?? false)
      : (chaseMissingTicketsDefaultOnSettingEnabled ?? false),
    liveEta: defaultProject
      ? (defaultProject.liveEta ?? false)
      : (liveEtaDefault ?? false),
    defaultDigitalTicketView: defaultProject?.defaultDigitalTicketView ?? false,
    prevailingWage: defaultProject?.prevailingWage ?? false,
    applyRateChangesToExistingOrders: defaultProject
      ? (defaultProject.applyRateChangesToExistingOrders ?? false)
      : (selectiveRateApplicationDefaultEnabled ?? false),
    vendorPayCommissionPercentage: defaultProject?.vendorPayCommissionPercentage,
    driverPayCommissionPercentage: defaultProject?.driverPayCommissionPercentage,
    materialRateItemized: defaultProject
      ? (defaultProject.materialRateItemized ?? false)
      : Boolean(recordCompanySettings?.isEnabled('itemize_material_rate_default_on')),
    taxRateSchedule: defaultProject?.taxRateSchedule
      ? toTaxRateScheduleFormValue(defaultProject.taxRateSchedule)
      : null,
    taxRate: defaultProject?.taxRate ?? null,
    taxFreight: defaultProject?.taxFreight ?? false,
    taxMaterial: defaultProject
      ? (defaultProject.taxMaterial ?? false)
      : Boolean(recordCompanySettings?.isEnabled('require_tax')),
    taxAddOns: defaultProject?.taxAddOns ?? false,
    salespeople: defaultProject?.salespeople || [],
  };
};
// Carry the InferType-derived shape AND the nine freight-rate side-channel
// fields. The rate fields are written into form state by the <FreightRates/>
// widget but are intentionally NOT part of the yup schema (they bypass the
// resolver). Keeping them as an intersection is the chosen fallback: a pure
// `yup.InferType<typeof projectFormSchema>` cascades into
// `useProjects/models.ts deparseUpdate` callers.
export type ProjectFormSchemaInterface = yup.InferType<typeof projectFormSchema> & {
  customerRate: RateDetailsDTO['targetRate'];
  customerRateType: RateDetailsDTO['targetRateType'];
  customerRateValue: RateDetailsDTO['targetRateValue'];
  vendorRate: RateDetailsDTO['targetRate'];
  vendorRateType: RateDetailsDTO['targetRateType'];
  vendorRateValue: RateDetailsDTO['targetRateValue'];
  driverRate: RateDetailsDTO['targetRate'];
  driverRateType: RateDetailsDTO['targetRateType'];
  driverRateValue: RateDetailsDTO['targetRateValue'];
};

// Drift detector: every key the schema produces MUST be reachable through
// `ProjectFormSchemaInterface`. If the yup schema gains a key but the type
// (or its intersection above) doesn't carry it, this assignment fails to
// compile. The runtime snapshot test in projectFormSchema.test.ts catches the
// inverse direction (a key removed from the schema must be removed from the
// expected set).
type _SchemaDriftCheck = [yup.InferType<typeof projectFormSchema>] extends [
  Pick<ProjectFormSchemaInterface, keyof yup.InferType<typeof projectFormSchema>>,
]
  ? true
  : never;
const _schemaDriftCheck: _SchemaDriftCheck = true;
void _schemaDriftCheck;

export function makeCustomerSpecificProjectFormSchema(
  tomlinsonOrderModalFeatureFlagEnabled: boolean,
) {
  let schema = projectFormSchema.clone();

  if (tomlinsonOrderModalFeatureFlagEnabled) {
    schema = schema.shape({
      haulerRateType: yup
        .object()
        .shape({ label: yup.string().required(), value: yup.string().required() })
        .required(),
    });
  }

  return schema;
}

const materialLineNeedsPickupSite = (
  material: { id?: string | null } | undefined,
  isDeleted?: boolean,
) => !isDeleted && Boolean(material?.id);

export const makeRequiredDepartmentSchema = () =>
  yup
    .object()
    .shape({ name: yup.string(), id: yup.string() })
    .nullable()
    .test(
      'has-department',
      t('form_validation_errors.required', { field: t('project.form.department') }),
      (department) => Boolean(department?.id),
    );

export const makeRequiredSalespeopleSchema = () =>
  yup
    .array()
    .of(yup.object().shape({ id: yup.string().required() }).required())
    .min(1, t('form_validation_errors.at_least_one_salesperson_required'));

export const MATERIAL_RATE_COVERAGE_ERROR_TYPE = 'material-rate-coverage';

export const toTaxRateScheduleFormValue = (schedule: TaxRateScheduleBasic) => ({
  label: TaxRateScheduleBasic.formatLabel(schedule.name, schedule.rate),
  value: schedule.id,
  rate: schedule.rate,
});

// A null rate still taxes; only a known 0% rate exempts the taxable-scope boxes.
const hasKnownZeroTaxRate = (taxRateSchedule: Nullable<TaxRateScheduleFormValue>) => {
  const rate = taxRateSchedule?._entity
    ? taxRateSchedule._entity.rate
    : taxRateSchedule?.rate;

  return !isNil(rate) && String(rate).trim() !== '' && Number(rate) === 0;
};

export const makeRequiredTaxSchema = (taxRateScheduleLabel: string) => ({
  taxRateSchedule: taxRateScheduleSchema.test(
    'has-tax-rate-schedule',
    t('form_validation_errors.required', { field: taxRateScheduleLabel }),
    (taxRateSchedule) => Boolean(taxRateSchedule?.value),
  ),
  taxFreight: yup
    .boolean()
    .notRequired()
    .test(
      'has-taxable-scope',
      t('form_validation_errors.at_least_one_taxable_scope_required'),
      function (taxFreight) {
        const { taxRateSchedule, taxMaterial, taxAddOns } = this.parent;

        return (
          hasKnownZeroTaxRate(taxRateSchedule) ||
          Boolean(taxFreight || taxMaterial || taxAddOns)
        );
      },
    ),
});

// Fast-Weigh rejects a project push missing a tax rate schedule or a material pickup site.
export function makeProjectFormSchema(
  fastWeighEnabled: boolean,
  recordCompanySettings?: CompanySettings,
  canManageMaterialRates = true,
) {
  let schema = projectFormSchema;

  if (recordCompanySettings?.isEnabled('require_department')) {
    schema = schema.shape({ department: makeRequiredDepartmentSchema() });
  }

  if (recordCompanySettings?.isEnabled('require_salesperson')) {
    schema = schema.shape({ salespeople: makeRequiredSalespeopleSchema() });
  }

  if (recordCompanySettings?.isEnabled('require_pickup_site')) {
    schema = schema.shape({
      pickUpWayPoint: waypointSchemaOptionalSites
        .shape({ _destroy: yup.boolean().notRequired() })
        .nullable()
        .test(
          'has-pickup-site',
          t('form_validation_errors.required', { field: t('form_fields.pick_up_site') }),
          (pickUpWayPoint) => {
            const site = pickUpWayPoint?.site;

            return Boolean(
              site?.id || site?.address?.streetAddress || site?.address?.placeId,
            );
          },
        ),
    });
  }

  const isMaterialRateRequiredFor = makeMaterialRateRequirement(
    recordCompanySettings,
    canManageMaterialRates,
  );
  if (isMaterialRateRequiredFor) {
    schema = schema
      .shape({
        materialRates: yup
          .array()
          .of(
            projectMaterialRateSchema.shape({
              materialRateBuyPrice: makeRatePriceSchema(),
              materialRateSellPrice: makeRatePriceSchema(),
            }),
          )
          .notRequired(),
      })
      .test(MATERIAL_RATE_COVERAGE_ERROR_TYPE, '', function (project) {
        if (!isMaterialRateRequiredFor(project.department)) return true;

        const liveRates = (project.materialRates ?? []).flatMap((rate, rateIndex) =>
          rate && !rate.isDeleted ? [{ rate, rateIndex }] : [],
        );
        const errors: yup.ValidationError[] = [];

        (project.projectMaterialTotals ?? []).forEach((total, totalIndex) => {
          if (!total || total.isDeleted || !total.material?.id) return;

          const ratesForTotal = liveRates.filter(
            ({ rate }) => rate.parentIndex === totalIndex,
          );
          if (!ratesForTotal.length) {
            errors.push(
              this.createError({
                path: `projectMaterialTotals[${totalIndex}].material`,
                message: t('form_validation_errors.material_rate_required'),
              }),
            );
          }

          ratesForTotal.forEach(({ rate, rateIndex }) => {
            const missingFields = [
              {
                isMissing: isNil(rate.materialRateSellPrice),
                field: 'materialRateSellPrice',
                label: t('administration.material_rates.sell_price'),
              },
              {
                isMissing: isNil(rate.materialRateBuyPrice),
                field: 'materialRateBuyPrice',
                label: t('administration.material_rates.buy_price'),
              },
              {
                isMissing: !rate.unitOfMeasure?.id,
                field: 'unitOfMeasure',
                label: t('form_fields.unit'),
              },
            ].filter(({ isMissing }) => isMissing);

            missingFields.forEach(({ field, label }) =>
              errors.push(
                this.createError({
                  path: `materialRates[${rateIndex}].${field}`,
                  message: requiredMessage(label),
                }),
              ),
            );
          });
        });

        return errors.length ? new yup.ValidationError(errors) : true;
      });
  }

  if (recordCompanySettings?.isEnabled('require_tax')) {
    schema = schema.shape(makeRequiredTaxSchema(t('project.form.tax_rate_schedule')));
  }

  if (!fastWeighEnabled) {
    return schema;
  }

  return schema.shape({
    projectMaterialTotals: yup
      .array()
      .of(
        projectMaterialTotalSchema
          .shape({
            site: projectMaterialTotalSiteSchema.when(['material', 'isDeleted'], {
              is: materialLineNeedsPickupSite,
              then: (schema) =>
                schema.test(
                  'has-pickup-site',
                  t('form_validation_errors.required', {
                    field: t('form_fields.pick_up_site'),
                  }),
                  (site) => Boolean(site?.id),
                ),
            }),
          })
          .notRequired(),
      )
      .notRequired(),
    taxRateSchedule: taxRateScheduleSchema.test(
      'has-tax-rate-schedule',
      t('form_validation_errors.required', {
        field: t('project.form.tax_rate_schedule'),
      }),
      (taxRateSchedule) => Boolean(taxRateSchedule?.value),
    ),
  });
}
